import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggerService } from './logger.service';

export interface LoadingState {
  isLoading: boolean;
  message?: string;
  progress?: number;
  type: 'spinner' | 'progress' | 'skeleton';
  context?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private readonly loadingSubject = new BehaviorSubject<LoadingState>({
    isLoading: false,
    type: 'spinner'
  });

  private loadingStates = new Map<string, LoadingState>();
  private readonly logger: LoggerService;

  constructor() {
    this.logger = (window as { loggerService?: LoggerService }).loggerService;
  }

  get loadingState$(): Observable<LoadingState> {
    return this.loadingSubject.asObservable();
  }

  get currentState(): LoadingState {
    return this.loadingSubject.value;
  }

  show(
    message?: string,
    type: 'spinner' | 'progress' | 'skeleton' = 'spinner',
    context?: string
  ): void {
    const state: LoadingState = {
      isLoading: true,
      message: message || 'Loading...',
      type,
      context
    };

    this.loadingSubject.next(state);

    if (this.logger) {
      this.logger.debug('Loading started', { message, type, context }, 'LOADING_SERVICE');
    }
  }

  hide(): void {
    const state: LoadingState = {
      isLoading: false,
      type: 'spinner'
    };

    this.loadingSubject.next(state);

    if (this.logger) {
      this.logger.debug('Loading stopped', null, 'LOADING_SERVICE');
    }
  }

  showProgress(message?: string, context?: string): void {
    const state: LoadingState = {
      isLoading: true,
      message: message || 'Processing...',
      progress: 0,
      type: 'progress',
      context
    };

    this.loadingSubject.next(state);
  }

  updateProgress(progress: number, message?: string): void {
    const currentState = this.loadingSubject.value;

    if (currentState.type === 'progress') {
      const newState: LoadingState = {
        ...currentState,
        progress: Math.min(Math.max(progress, 0), 100),
        message: message || currentState.message
      };

      this.loadingSubject.next(newState);
    }
  }

  showForContext(
    context: string,
    message?: string,
    type: 'spinner' | 'progress' | 'skeleton' = 'spinner'
  ): void {
    const state: LoadingState = {
      isLoading: true,
      message: message || 'Loading...',
      type,
      context
    };

    this.loadingStates.set(context, state);
    this.updateGlobalState();

    if (this.logger) {
      this.logger.debug(
        `Loading started for context: ${context}`,
        { message, type },
        'LOADING_SERVICE'
      );
    }
  }

  hideForContext(context: string): void {
    this.loadingStates.delete(context);
    this.updateGlobalState();

    if (this.logger) {
      this.logger.debug(`Loading stopped for context: ${context}`, null, 'LOADING_SERVICE');
    }
  }

  isContextLoading(context: string): boolean {
    return this.loadingStates.has(context);
  }

  showSkeleton(message?: string, context?: string): void {
    this.show(message, 'skeleton', context);
  }

  updateMessage(message: string): void {
    const currentState = this.loadingSubject.value;

    if (currentState.isLoading) {
      const newState: LoadingState = {
        ...currentState,
        message
      };

      this.loadingSubject.next(newState);
    }
  }

  showWithTimeout(message: string, timeout: number = 10000): void {
    this.show(message);

    setTimeout(() => {
      if (this.currentState.isLoading) {
        this.hide();

        if (this.logger) {
          this.logger.warn(
            `Loading timeout after ${timeout}ms`,
            { message, timeout },
            'LOADING_SERVICE'
          );
        }
      }
    }, timeout);
  }

  private updateGlobalState(): void {
    if (this.loadingStates.size > 0) {
      const firstContext = Array.from(this.loadingStates.values())[0];
      this.loadingSubject.next({
        isLoading: true,
        message: firstContext.message,
        type: firstContext.type,
        context: 'multiple'
      });
    } else {
      this.hide();
    }
  }

  getActiveContexts(): string[] {
    return Array.from(this.loadingStates.keys());
  }

  getContextCount(): number {
    return this.loadingStates.size;
  }

  clearAll(): void {
    this.loadingStates.clear();
    this.hide();

    if (this.logger) {
      this.logger.debug('All loading contexts cleared', null, 'LOADING_SERVICE');
    }
  }

  showForForm(context: string, message?: string): void {
    this.showForContext(context, message || 'Saving...', 'spinner');
  }

  showForDataLoad(context: string, message?: string): void {
    this.showForContext(context, message || 'Loading data...', 'spinner');
  }

  showForFileUpload(context: string, message?: string): void {
    this.showForContext(context, message || 'Uploading file...', 'progress');
  }

  showForExport(context: string, message?: string): void {
    this.showForContext(context, message || 'Exporting...', 'progress');
  }
}
